import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import Env from "../runtimeEnv";

const categories = [
  { value: "feedback", label: "Feedback" },
  { value: "support", label: "Support request" },
  { value: "account_deletion", label: "Account/data deletion" },
];

const ContactModal = ({
  isOpen,
  onClose,
  defaultEmail = "",
  initialCategory = "feedback",
}) => {
  const [email, setEmail] = useState(defaultEmail);
  const [category, setCategory] = useState(initialCategory);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const dialogRef = useRef(null);
  const availableCategories =
    initialCategory === "account_deletion"
      ? categories
      : categories.filter((option) => option.value !== "account_deletion");
  const selectedCategoryLabel =
    categories.find((option) => option.value === category)?.label || "Message";

  useEffect(() => {
    if (!isOpen) return undefined;

    setEmail(defaultEmail || "");
    setCategory(initialCategory);
    setMessage(
      initialCategory === "account_deletion"
        ? "Please delete my Plutonic account and associated data."
        : ""
    );
    setStatus("idle");
    setError("");
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    window.setTimeout(() => dialogRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [defaultEmail, initialCategory, isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("sending");
    setError("");

    try {
      await axios.post(
        `${Env.REACT_APP_PYTHON_API_URL}/api/contact`,
        { email: email.trim(), category, message: message.trim() },
        { withCredentials: true }
      );
      setStatus("sent");
    } catch (requestError) {
      setStatus("error");
      setError(
        requestError.response?.data?.message ||
          "We couldn't send your message. Please try again."
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && status !== "sending") onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-dialog-title"
        tabIndex="-1"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl outline-none sm:p-8"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="contact-dialog-title" className="text-2xl font-semibold text-gray-900">
              {status === "sent" ? `${selectedCategoryLabel} sent` : "Contact us"}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {status === "sent"
                ? "Thank you. Our team will follow up by email."
                : "Send a note to the Plutonic team. We’ll reply by email."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={status === "sending"}
            aria-label="Close contact form"
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {status === "sent" ? (
          <div className="mt-8" aria-live="polite">
            <div className="rounded-xl bg-green-50 p-4 text-green-800">
              Your {selectedCategoryLabel.toLowerCase()} has been sent.
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full rounded-full bg-[#22AAEE] px-6 py-3 font-semibold text-white hover:bg-[#168dcc]"
            >
              Done
            </button>
          </div>
        ) : (
          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="contact-category" className="block text-sm font-semibold text-gray-800">
                What can we help with?
              </label>
              <select
                id="contact-category"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                disabled={status === "sending"}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-3 focus:border-[#22AAEE] focus:outline-none focus:ring-2 focus:ring-[#22AAEE]/30"
              >
                {availableCategories.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-800">
                Email associated with your account
              </label>
              <input
                id="contact-email"
                type="email"
                autoComplete="email"
                required
                maxLength="254"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={status === "sending"}
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-3 focus:border-[#22AAEE] focus:outline-none focus:ring-2 focus:ring-[#22AAEE]/30"
              />
            </div>

            <div>
              <div className="flex items-center justify-between gap-4">
                <label htmlFor="contact-message" className="block text-sm font-semibold text-gray-800">
                  Message
                </label>
                <span className="text-xs text-gray-500">{message.length}/4000</span>
              </div>
              <textarea
                id="contact-message"
                required
                minLength="10"
                maxLength="4000"
                rows="6"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                disabled={status === "sending"}
                placeholder="Tell us what’s on your mind…"
                className="mt-2 w-full resize-y rounded-lg border border-gray-300 px-3 py-3 focus:border-[#22AAEE] focus:outline-none focus:ring-2 focus:ring-[#22AAEE]/30"
              />
            </div>

            {category === "account_deletion" && (
              <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                We may ask you to verify account ownership. Records required by
                healthcare, billing, security, or other laws may still be retained.
              </p>
            )}

            {error && (
              <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full rounded-full bg-[#22AAEE] px-6 py-3 font-semibold text-white hover:bg-[#168dcc] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "sending" ? "Sending…" : "Send request"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
